import { Observable, defer, of, timer } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import {
  PermissionCacheLogger,
  PermissionCacheStorage,
  permissionCacheKey,
  savePermissionsToCache,
} from './permission-cache';

/**
 * #34116: back-off before each retry of a failed push-path permission GET (so 3 attempts in all, ~4s). Short on
 * purpose: the push means an admin just changed this user's access, and the cache is already evicted, so a
 * failure only costs a re-fetch on the next page load.
 */
export const PUSH_REFRESH_RETRY_DELAYS_MS: readonly number[] = [1_000, 3_000];

/**
 * #34116: a monotonic load counter shared by every permission load (cache-first sign-in and push refresh).
 * {@link begin} starts a load and returns a check that stays true only while no newer load has begun (or
 * {@link invalidate} was called, e.g. on sign-out), so a slower, older GET can never apply or re-cache its
 * possibly pre-revocation answer over a newer one.
 */
export class PermissionLoadGeneration {
  private latest = 0;

  begin(): () => boolean {
    const generation = ++this.latest;
    return () => generation === this.latest;
  }

  invalidate(): void {
    this.latest++;
  }
}

/** Emitted on `SecurityService.permissionsRefreshFailed$` when a push-path reload gives up after its retries. */
export interface PermissionsRefreshFailure {
  /** `identityKey()` of the user whose reload failed. */
  identityKey: string;
  error: unknown;
}

export interface PushRefreshOptions {
  /** `identityKey()` of the user to reload; also selects the cache entry to evict. */
  identityKey: string;
  storage: PermissionCacheStorage;
  log: PermissionCacheLogger & { error(...args: unknown[]): void };
  /** A cold server GET of the user's permissions; re-subscribed on each retry. */
  fetch: () => Observable<unknown>;
  /** False once the user signed out or switched, so a late answer is never applied to someone else. */
  isStillCurrent: () => boolean;
  apply: (data: Array<unknown>) => void;
  onFailure: (failure: PermissionsRefreshFailure) => void;
}

/**
 * #34116 push path: a server-only, non-destructive permission reload. Unlike the cache-first sign-in load it
 * (1) evicts the `ups_security_*` cache entry first, so the pre-change copy can never be re-applied, even after
 * a page reload while the GET is failing; (2) never clears the in-memory permissions, so there is no Cleared
 * blackout: the current set stays until the server answer swaps in; (3) retries a failed GET with
 * {@link PUSH_REFRESH_RETRY_DELAYS_MS}, then logs and reports a {@link PermissionsRefreshFailure}. Emits true
 * once the server data is applied, false otherwise.
 */
export function refreshPermissionsFromServer(
  options: PushRefreshOptions
): Observable<boolean> {
  const { storage, log } = options;
  const cacheKey = permissionCacheKey(options.identityKey);
  return defer(() => {
    storage.removeItem(cacheKey);
    return options.fetch();
  }).pipe(
    retry({
      count: PUSH_REFRESH_RETRY_DELAYS_MS.length,
      delay: (_error, attempt) =>
        timer(PUSH_REFRESH_RETRY_DELAYS_MS[attempt - 1]),
    }),
    map((data) => {
      if (!options.isStillCurrent()) {
        return false;
      }
      const permissions = data as Array<unknown>;
      options.apply(permissions);
      savePermissionsToCache(storage, log, cacheKey, permissions);
      return true;
    }),
    catchError((error: unknown) => {
      log.error(
        'SecurityService: push-triggered permission refresh failed after retries; cache left evicted',
        error
      );
      options.onFailure({ identityKey: options.identityKey, error });
      return of(false);
    })
  );
}
